import React, { useState, useMemo } from 'react';
import { PropTypes } from 'prop-types';
import { Link } from 'react-router-dom';
import {
  Box,
  Stack,
  Grid,
  Typography,
  useMediaQuery,
  Tabs,
  Tab,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import MailIcon from '@mui/icons-material/Mail';

import IconFeedback from 'app/components/customSvgs/IconFeedback';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import IconAcctFeeds from 'app/components/customSvgs/IconAcctFeeds';
import IconFilePlayer from 'app/components/customSvgs/IconFilePlayer';
import IconListings from 'app/components/customSvgs/IconListings';
import IconCustomers from 'app/components/customSvgs/IconCustomers';
import IconFileManager from 'app/components/customSvgs/IconFileManager';
import IconFeaturedListings from 'app/components/customSvgs/IconFeaturedListings';
import IconReports from 'app/components/customSvgs/IconReports';
import IconManageChatCard from 'app/components/customSvgs/IconManageChatCard';

import { toNumber, toCurrency } from 'app/helpers/currencyHelpers';
import { useGetAdvisorQuery } from 'app/api/mainApi';
import useProfileImages from 'app/hooks/useProfileImages';
import TabContent from 'app/components/TabContent';
import camelizeKeys from 'app/util/camelizeKeys';
import IconCard from './IconCard';
import AdvisorDashboard from './AdvisorDashboard';
import AdvisorInsights from './AdvisorInsights';
import StickyAdvisorHeader from './StickyAdvisorHeader';
import AdvisorGrowBusinessBanner from './AdvisorGrowBusinessBanner';

const CustomTabs = styled(Tabs)(() => ({
  '&.MuiTabs-root': {
    borderBottom: '1px solid var(--color_ddd)',
    display: 'flex',
    '& .MuiTab-root': {
      flexGrow: '1',
      minWidth: 'auto',
    },
    '& .MuiButtonBase-root': {
      padding: '0px',
    },
  },
}));

const AdvisorAccount = ({ appConfig }) => {
  const {
    currentUser,
    unreadMailCount,
    txnUrl,
    availableBalance: rawAvailableBalance,
  } = useMemo(() => camelizeKeys(appConfig), [appConfig]);

  const { data: advisor } = useGetAdvisorQuery({ login: currentUser?.login }, { skip: !currentUser?.login });
  const { acceptedAvatarImage: avatarImage } = advisor ? useProfileImages(advisor) : { acceptedAvatarImage: '' };

  const availableBalance = toNumber(rawAvailableBalance);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [accordion, setAccordion] = useState('promote');

  const [selectedTab, setSelectedTab] = React.useState(0);
  const handleChange = (_e, newValue) => {
    setSelectedTab(newValue);
  };

  // pass `href` if we want an <a href> because it's not part of our react app
  // pass `to` if we want a <Link to> because it's a react-routed page
  // If you use href when you shouldn't, then you'll clobber the redux store and make some unneccessary requests
  // If you use to when you shouldn't, then you get weird unpredictable behavior
  const shortcuts = [
    {
      icon: <MailIcon />,
      label: 'Mail',
      badge: unreadMailCount,
      href: '/messages/inbox',
    },
    {
      icon: <IconListings />,
      label: 'Listings',
      href: '/account/my_listings',
    },
    {
      icon: <IconCustomers />,
      label: 'Customers',
      to: '/my_customers',
    },
    {
      icon: <IconFeedback />,
      label: 'Feedback',
      to: '/account/feedbacks',
    },
    {
      icon: <IconFileManager />,
      label: 'File Manager',
      href: '/account/filemanager',
    },
    {
      icon: <img src="/plus_frontend/assets/svg/ico-goodies2.svg" alt="Goodies" className="nav-icon" />,
      label: 'Goodies',
      href: '/account/filemanager#tab-goody',
    },
    {
      icon: <IconFeaturedListings />,
      label: 'Featured Listings',
      href: '/account/featured_listing_results/listings',
    },
    {
      icon: <IconReports />,
      label: 'Reports',
      href: '/performance/reports',
    },
    {
      icon: <IconManageChatCard />,
      label: 'Manage Chat Card',
      href: '/account/chat_profile/0/description', // TODO: We should change the urls for chat profiles to not even refer to a chat_profile_id
    },
    {
      icon: <CreditCardIcon />,
      label: 'Manage Credit Cards',
      href: `${txnUrl}/cards?return_to=account`,
    },
    {
      icon: <IconAcctFeeds />,
      label: 'Feeds',
      to: `/feed/${currentUser?.login}`,
    },
    {
      icon: <IconFilePlayer />,
      label: 'File Player',
      href: '/account/fileplayer',
    },
    {
      icon: <img src="/niteflirt/images/nav_icons/advisor_help.png" alt="Help" />,
      label: 'Flirt Help',
      href: 'http://support.niteflirt.com/hc/en-us/categories/201835568-Help-for-Flirts',
    },
    {
      icon: <img src="/niteflirt/images/nav_icons/affiliate_program.png" alt="Affiliate" />,
      label: 'Affiliate',
      href: appConfig?.currentUser?.affiliate ? `${appConfig.affiliateUrl}/creative` : appConfig.affiliateUrl,
    },
  ];

  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const [viewAllShortcuts, setViewAllShortcuts] = useState(false);
  const visibleShortcuts = (isSmallScreen && !viewAllShortcuts) ? shortcuts.slice(0, 3) : shortcuts;

  return (
    <>
      <StickyAdvisorHeader />
      <Box
        display="flex"
        flexDirection="row"
        alignItems="center"
        justifyContent="space-between"
        className="seeker-account-container"
        sx={{ p: 2, my: 2, borderBottom: '1px solid #ddd' }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          { isSmallScreen ? (
            <>
              <Box className="menu-initials menu-avatar" style={{ backgroundImage: `url('${avatarImage}')` }}>
                  &nbsp;
              </Box>
              <Box>
                <Typography variant="h4">
                  {currentUser.login}
                </Typography>
              </Box>
            </>
          ) : (
            <Typography variant="h4" sx={{ pl: 1 }}>
              Welcome&nbsp;
              {currentUser.login}
            </Typography>
          )}
        </Stack>
        <Box
          display="flex"
          flexDirection="row"
          alignItems="center"
        >
          <Box display="flex" flexDirection="row">
            <Stack
              direction="column"
              spacing={0}
              alignItems="flex-start"
              sx={{ mr: 3 }}
            >
              <Box sx={{ color: 'var(--color_gray_nine)', fontSize: '0.875em' }}>
                Balance
              </Box>
              <Box>
                {toCurrency(availableBalance)}
              </Box>
              <Box>
                <Link to="/transactions/details">
                  Transaction Details
                </Link>
              </Box>
            </Stack>
          </Box>
        </Box>
      </Box>

      <Grid
        container
        spacing={1.75}
        justifyContent="flex-start"
        sx={{
          px: 2,
          mb: 2,
          maxWidth: 900,
          mx: 'auto',
          '@media (max-width: 1200px)': { maxWidth: 800 },
          '@media (max-width: 900px)': { maxWidth: 800 },
          '@media (max-width: 600px)': { maxWidth: 360, px: 0 },
        }}
      >
        {visibleShortcuts.map((shortcut) => (
          <Grid item key={shortcut.label}>
            <IconCard {...shortcut} />
          </Grid>
        ))}
      </Grid>

      { isSmallScreen && (
        <Typography
          variant="body1"
          align="center"
          sx={{ pb: 1, cursor: 'pointer', color: 'var(--link_color)' }}
          onClick={() => setViewAllShortcuts(!viewAllShortcuts)}
        >
          { viewAllShortcuts ? 'View less' : 'View more' }
        </Typography>
      )}

      <AdvisorGrowBusinessBanner
        open={drawerOpen}
        setOpen={setDrawerOpen}
        accordion={accordion}
        setAccordion={setAccordion}
      />

      <Box sx={{ mt: 0.5 }}>
        <CustomTabs
          value={selectedTab}
          onChange={handleChange}
          aria-label="Account Tabs"
        >
          <Tab label="Dashboard" id="simple-tab-0" aria-controls="simple-tabpanel-0" sx={{ maxWidth: '150px' }} />
          <Tab label="Insights" id="simple-tab-1" aria-controls="simple-tabpanel-1" sx={{ maxWidth: '150px' }} />
        </CustomTabs>

        <TabContent value={selectedTab} index={0} padding={0}>
          <AdvisorDashboard
            setDrawerOpen={setDrawerOpen}
            setAccordion={setAccordion}
          />
        </TabContent>
        <TabContent value={selectedTab} index={1} padding={0}>
          <AdvisorInsights currentUser={currentUser} />
        </TabContent>
      </Box>
    </>
  );
};

AdvisorAccount.propTypes = {
  appConfig: PropTypes.object.isRequired,
};

export default AdvisorAccount;
